import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { CONFIRM_DIALOG_EVENT, type ConfirmDialogDetail } from '../../lib/confirmDialog';

const inferTone = (message: string): 'danger' | 'warning' | 'primary' => {
  const normalized = message.toLocaleLowerCase('tr-TR');
  if (/(sil|kaldır|iptal|sıfırla|geri alınamaz)/.test(normalized)) return 'danger';
  if (/(gönder|oluştur|değiştir|onay)/.test(normalized)) return 'warning';
  return 'primary';
};

const ConfirmDialog: React.FC = () => {
  const [request, setRequest] = useState<ConfirmDialogDetail | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleRequest = (event: Event) => {
      const nextRequest = (event as CustomEvent<ConfirmDialogDetail>).detail;
      setRequest(current => {
        current?.resolve(false);
        return nextRequest;
      });
    };

    window.addEventListener(CONFIRM_DIALOG_EVENT, handleRequest);
    return () => window.removeEventListener(CONFIRM_DIALOG_EVENT, handleRequest);
  }, []);

  useEffect(() => {
    if (!request) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => cancelButtonRef.current?.focus(), 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish(false);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [request]);

  const finish = (confirmed: boolean) => {
    if (!request) return;
    request.resolve(confirmed);
    setRequest(null);
  };

  if (!request) return null;

  const tone = request.tone || inferTone(request.message);
  const isDanger = tone === 'danger';
  const title = request.title || (isDanger ? 'İşlemi onaylayın' : 'Devam edilsin mi?');
  const confirmLabel = request.confirmLabel || (isDanger ? 'Evet, işlemi yap' : 'Onayla');
  const Icon = isDanger ? AlertTriangle : CheckCircle2;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/55 backdrop-blur-sm animate-in fade-in duration-150"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && finish(false)}
    >
      <section
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        className="w-full max-w-md overflow-hidden rounded-3xl border border-white/60 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl shadow-slate-950/25 animate-in zoom-in-95 duration-150"
      >
        <div className={`h-1.5 ${isDanger ? 'bg-gradient-to-r from-rose-500 to-orange-400' : 'bg-gradient-to-r from-blue-600 to-indigo-500'}`} />
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${isDanger ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400' : 'bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400'}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 id="confirm-dialog-title" className="text-lg font-bold tracking-tight text-slate-950 dark:text-white">
                {title}
              </h2>
              <p id="confirm-dialog-description" className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {request.message}
              </p>
            </div>
            <button
              type="button"
              onClick={() => finish(false)}
              className="-mr-1 -mt-1 rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label="Kapat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              ref={cancelButtonRef}
              type="button"
              onClick={() => finish(false)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200/70 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {request.cancelLabel || 'Vazgeç'}
            </button>
            <button
              type="button"
              onClick={() => finish(true)}
              className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg focus:outline-none focus:ring-4 ${isDanger ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/20 focus:ring-rose-200' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20 focus:ring-blue-200'}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConfirmDialog;
